/**
 * A context-based hook that allows components to determine the view of the
 * "IOL" page. The API is the same as useContext.
 */

import React, { useState, useCallback, useContext, createContext } from "react"
import PropTypes from "prop-types"
import useLocalStorage from "./use-local-storage"
import { defaultIOLTableColumns } from "../data/iol-table-columns"

const IOLViewPreferencesContext = createContext()

export const IOLViewPreferencesProvider = props => {
  const defaultSavedPreferencesConfig = new Map([
    ["stripes", true],
    ["columns", defaultIOLTableColumns],
  ]);
  // Get shipment view preferences out of local storage
  const [savedPreferences, persistPreferences] = useLocalStorage(
    "iol-view-preferences"
  )

  // Initialize state value falling back to the default values defined above
  const [preferences, setPreferences] = useState(
    // Revive preference map from local storage if it exists
    savedPreferences
      // add new columns to local storage if the user has outdated column structure
      ? new Map([
        ["stripes", new Map(savedPreferences).get("stripes")],
        ["columns", { ...defaultSavedPreferencesConfig.get("columns"), ...new Map(savedPreferences).get("columns") }],
      ])
      : defaultSavedPreferencesConfig
  )

  // When the user changes the preferences, persist the value to local storage
  // and update the state value
  const changePreferences = useCallback(
    values => {
      setPreferences(values)
      // Convert the values Map to an array when putting into local storage
      // so it can be serialized with JSON.stringify
      persistPreferences(Array.from(values.entries()))
    },
    [persistPreferences]
  )

  return (
    <IOLViewPreferencesContext.Provider
      value={[preferences, changePreferences]}
    >
      {props.children}
    </IOLViewPreferencesContext.Provider>
  )
}

IOLViewPreferencesProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

const useIOLViewPreferences = () =>
  useContext(IOLViewPreferencesContext)

export default useIOLViewPreferences